/**
 * The scope the Tags tab reads every threshold through (pure).
 * For Media Devoted | Created By Ciro Autiero
 *
 * The preview response already carries the campaign, its tracker and the
 * SMSWiser country it resolved to — the same triple the chips use — so reading
 * the scope from there guarantees this tab and the ⚡ chips agree.
 */

import type { FlowOptimizerPreviewResult } from '@trafficpulse/shared';
import type { TagRuleScope } from '@/features/tag-rule-scope';
import { tagProviderOf } from '@/features/tag-rule-scope/model/tag-provider';

import type { FlowOptimizerProvider } from '../../../services/flow-optimizer-api';

export interface TagsScopeState {
  scope: TagRuleScope;
  /** '' when the campaign is unlinked, or shared across countries. */
  countryKey: string;
  /**
   * True when no country could be resolved for this campaign.
   *
   * `resolveCampaignCountryKey` returns '' for a campaign that is not linked to
   * an SMSWiser country, or that is linked to more than one. The rules still
   * apply and a campaign-level override still wins — but the COUNTRY tier drops
   * out of the chain, so a threshold that looks inherited here may be
   * overridden for that country elsewhere. Worth saying rather than leaving
   * someone to wonder why a number differs from the admin page.
   */
  countryUnresolved: boolean;
}

export function tagsScopeFrom(
  result: FlowOptimizerPreviewResult | null,
  campaignId: string,
  provider: FlowOptimizerProvider,
): TagsScopeState {
  const countryKey = (result?.countryKey ?? '').trim();
  // The drawer speaks lowercase; override rows are keyed by the enum. A
  // tracker without tag overrides (Tres Commas) gets no provider — never
  // VOLUUM — and the drawer does not offer its Tags tab at all.
  const tagProvider = tagProviderOf(provider);
  const scope: TagRuleScope = {
    ...(countryKey ? { countryKey } : {}),
    ...(tagProvider ? { provider: tagProvider } : {}),
    campaignId,
  };
  return { scope, countryKey, countryUnresolved: !countryKey };
}
